import {
  type AiOperationsRun,
  aiOperationsRunDetailResponseSchema,
  aiOperationsRunsResponseSchema,
  type TriggerAiRunResponse,
  type TriggerAiRunsBulkResponse,
  triggerAiRunResponseSchema,
  triggerAiRunsBulkResponseSchema,
} from "@adpilot/shared";
import { assertAiOperationsMockWritable, isAiOperationsMockEnabled } from "@/mocks/enabled";
import { type ReportingPeriod, resolveReportingPeriod } from "@/stores/reporting-period";
import { api } from "./api";
import {
  addReportingDays,
  type ReportingTimeZone,
  zonedStartOfDayUtc,
} from "./reporting-time-zone";

const ACTIVE_RUN_REFRESH_MS = 2_000;

export function aiOperationsRefetchInterval(
  runs: AiOperationsRun[] | undefined,
  pendingBatchId: string | null,
): number | false {
  if (pendingBatchId) return ACTIVE_RUN_REFRESH_MS;
  return runs?.some((run) => run.status === "running") ? ACTIVE_RUN_REFRESH_MS : false;
}

export async function triggerAiRun(campaignId: string): Promise<TriggerAiRunResponse> {
  assertAiOperationsMockWritable();
  const response = await api
    .post("ai-runs", { json: { campaignId } })
    .json<{ success: true; data: unknown }>();
  return triggerAiRunResponseSchema.parse(response.data);
}

export async function triggerAiRunsBulk(
  campaignIds: string[],
  /** ADP-270: demo deployments only; absent → the deployment default. */
  modelMode?: "demo_standard" | "live",
): Promise<TriggerAiRunsBulkResponse> {
  assertAiOperationsMockWritable();
  const response = await api
    .post("ai-runs/bulk", { json: { campaignIds, ...(modelMode ? { modelMode } : {}) } })
    .json<{ success: true; data: unknown }>();
  return triggerAiRunsBulkResponseSchema.parse(response.data);
}

/** ADP 回滚:对一条已执行的竞价动作创建反向补偿动作并执行。 */
export async function rollbackAiRunAction(
  runId: string,
  actionId: string,
): Promise<{ rollbackActionId: string; replayed: boolean }> {
  assertAiOperationsMockWritable();
  const response = await api
    .post(`ai-runs/${runId}/actions/${actionId}/rollback`)
    .json<{ success: true; data: { rollbackActionId: string; replayed: boolean } }>();
  return response.data;
}

export async function fetchAiOperationsRuns(
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
  shopIds?: string[],
) {
  // 内联 DEV 字面量让 Rollup 在生产构建里整段裁剪，避免 mock chunk 被打进产物；
  // 跨模块的 isAiOperationsMockEnabled() 调用无法被常量折叠。
  if (import.meta.env.DEV && isAiOperationsMockEnabled()) {
    const { AI_OPERATIONS_MOCK_RUNS } = await import("@/mocks/ai-operations");
    return AI_OPERATIONS_MOCK_RUNS;
  }
  const { startedAfter, startedBefore } = runRangeUtc(range, timeZone);
  const response = await api
    .get("ai-runs", {
      searchParams: {
        startedAfter,
        startedBefore,
        limit: 500,
        // ADP-110: aggregate scope; empty/omitted keeps the single-shop path.
        ...(shopIds?.length ? { shopIds: shopIds.join(",") } : {}),
      },
    })
    .json<{ success: true; data: unknown }>();
  return aiOperationsRunsResponseSchema.parse(response.data).runs;
}

export async function fetchAiOperationsRun(runId: string) {
  if (import.meta.env.DEV && isAiOperationsMockEnabled()) {
    const { getAiOperationsMockRunDetail } = await import("@/mocks/ai-operations");
    return getAiOperationsMockRunDetail(runId);
  }
  const response = await api.get(`ai-runs/${runId}`).json<{ success: true; data: unknown }>();
  return aiOperationsRunDetailResponseSchema.parse(response.data);
}

export function runRangeUtc(
  range: ReportingPeriod,
  timeZone: ReportingTimeZone,
  now = new Date(),
): {
  startedAfter: string;
  startedBefore: string;
} {
  const resolved = resolveReportingPeriod(range, now, timeZone);
  return {
    startedAfter: zonedStartOfDayUtc(resolved.startDate, timeZone).toISOString(),
    startedBefore: zonedStartOfDayUtc(
      addReportingDays(resolved.endDate, 1),
      timeZone,
    ).toISOString(),
  };
}
